import { SafeParseError, z, ZodIssueCode } from "zod";

const getOutboundCommercial = (t: any) => ({
  shape: z.object({
    outbound_cabin: z.string().optional(),
    outbound_flight_number: z.string(),
    outbound_flight_pnr: z.string().optional(),
    outbound_departure_airport_code: z.string(),
    outbound_departure_airport_name: z.string(),
    outbound_arrival_airport_code: z.string(),
    outbound_arrival_airport_name: z.string(),
    outbound_departure_time: z.string(),
    outbound_arrival_time: z.string(),
    outbound_departure_date: z.string(),
    outbound_arrival_date: z.string(),
    outbound_duration_hours: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
    outbound_duration_minutes: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
  }),
  schema: z.object({
    outbound_cabin: z.string().optional(),
    outbound_flight_number: z
      .string()
      .min(1, t("flightForm.validation.flightNumberRequired")),
    outbound_flight_pnr: z.string().optional(),
    outbound_departure_airport_code: z
      .string()
      .min(1, t("flightForm.validation.departureAirportCodeRequired")),
    outbound_departure_airport_name: z
      .string()
      .min(1, t("flightForm.validation.departureAirportNameRequired")),
    outbound_arrival_airport_code: z
      .string()
      .min(1, t("flightForm.validation.arrivalAirportCodeRequired")),
    outbound_arrival_airport_name: z
      .string()
      .min(1, t("flightForm.validation.arrivalAirportNameRequired")),
    outbound_departure_time: z
      .string()
      .min(1, t("flightForm.validation.departureTimeRequired")),
    outbound_arrival_time: z
      .string()
      .min(1, t("flightForm.validation.arrivalTimeRequired")),
    outbound_departure_date: z
      .string()
      .min(1, t("flightForm.validation.departureDateRequired")),
    outbound_arrival_date: z
      .string()
      .min(1, t("flightForm.validation.arrivalDateRequired")),
    outbound_duration_hours: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
    outbound_duration_minutes: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
  }),
});

const getOutboundPrivateJet = (t: any) => ({
  schema: z.object({
    outbound_order_number: z.string().optional(),
    outbound_aircraft: z.string().optional(),
    outbound_tail_number: z
      .string()
      .min(1, t("flightForm.validation.tailNumberRequired")),
    outbound_call_sign: z.string().optional(),
    outbound_operator: z
      .string()
      .min(1, t("flightForm.validation.operatorRequired")),
    outbound_departure_date: z
      .string()
      .min(1, t("flightForm.validation.departureDateRequired")),
    outbound_departure_time: z
      .string()
      .min(1, t("flightForm.validation.departureTimeRequired")),
    outbound_departure_location: z
      .string()
      .min(1, t("flightForm.validation.departureLocationRequired")),
    outbound_arrival_location: z
      .string()
      .min(1, t("flightForm.validation.arrivalLocationRequired")),
    outbound_arrival_date: z
      .string()
      .min(1, t("flightForm.validation.arrivalDateRequired")),
    outbound_arrival_time: z
      .string()
      .min(1, t("flightForm.validation.arrivalTimeRequired")),
    outbound_duration_hours: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
    outbound_duration_minutes: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
    outbound_handling_agents_country_code: z.string().optional(),
    outbound_handling_agents: z.string().optional(),
    outbound_handling_agents_contact_number: z.string().optional(),
  }),
  shape: z.object({
    outbound_order_number: z.string().optional(),
    outbound_aircraft: z.string().optional(),
    outbound_tail_number: z.string(),
    outbound_call_sign: z.string().optional(),
    outbound_operator: z.string(),
    outbound_departure_date: z.string(),
    outbound_departure_time: z.string(),
    outbound_departure_location: z.string().optional(),
    outbound_arrival_location: z.string(),
    outbound_arrival_date: z.string(),
    outbound_arrival_time: z.string(),
    outbound_duration_hours: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
    outbound_duration_minutes: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),

    outbound_handling_agents_country_code: z.string().optional(),
    outbound_handling_agents: z.string().optional(),
    outbound_handling_agents_contact_number: z.string().optional(),
  }),
});

const getReturnCommercial = (t: any) => ({
  schema: z.object({
    return_cabin: z.string().optional(),
    return_flight_number: z
      .string()
      .min(1, t("flightForm.validation.flightNumberRequired")),
    return_flight_pnr: z.string().optional(),
    return_departure_airport_code: z
      .string()
      .min(1, t("flightForm.validation.departureAirportCodeRequired"))
      .nullable(),
    return_departure_airport_name: z
      .string()
      .min(1, t("flightForm.validation.departureAirportNameRequired"))
      .nullable(),
    return_arrival_airport_code: z
      .string()
      .min(1, t("flightForm.validation.arrivalAirportCodeRequired"))
      .nullable(),
    return_arrival_airport_name: z
      .string()
      .min(1, t("flightForm.validation.arrivalAirportNameRequired"))
      .nullable(),
    return_departure_time: z
      .string()
      .min(1, t("flightForm.validation.departureTimeRequired")),
    return_arrival_time: z
      .string()
      .min(1, t("flightForm.validation.arrivalTimeRequired")),
    return_departure_date: z
      .string()
      .min(1, t("flightForm.validation.departureDateRequired")),
    return_arrival_date: z
      .string()
      .min(1, t("flightForm.validation.arrivalDateRequired")),
    return_duration_hours: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
    return_duration_minutes: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
  }),
  shape: z.object({
    return_cabin: z.string().optional(),
    return_flight_number: z.string(),
    return_flight_pnr: z.string().optional(),
    return_departure_airport_code: z.string().nullable(),
    return_departure_airport_name: z.string().nullable(),
    return_arrival_airport_code: z.string().nullable(),
    return_arrival_airport_name: z.string().nullable(),
    return_departure_time: z.string(),
    return_arrival_time: z.string(),
    return_departure_date: z.string(),
    return_arrival_date: z.string(),
    return_duration_hours: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
    return_duration_minutes: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
  }),
});

const getReturnPrivateJet = (t: any) => ({
  schema: z.object({
    return_order_number: z.string().optional(),
    return_aircraft: z.string().optional(),
    return_tail_number: z
      .string()
      .min(1, t("flightForm.validation.tailNumberRequired")),
    return_call_sign: z.string().optional(),
    return_operator: z
      .string()
      .min(1, t("flightForm.validation.operatorRequired")),
    return_departure_date: z
      .string()
      .min(1, t("flightForm.validation.departureDateRequired")),
    return_departure_time: z
      .string()
      .min(1, t("flightForm.validation.departureTimeRequired")),
    return_departure_location: z
      .string()
      .min(1, t("flightForm.validation.departureLocationRequired")),
    return_arrival_location: z
      .string()
      .min(1, t("flightForm.validation.arrivalLocationRequired")),
    return_arrival_date: z
      .string()
      .min(1, t("flightForm.validation.arrivalDateRequired")),
    return_arrival_time: z
      .string()
      .min(1, t("flightForm.validation.arrivalTimeRequired")),
    return_duration_hours: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
    return_duration_minutes: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
    return_handling_agents_country_code: z.string().optional().optional(),
    return_handling_agents: z.string().optional().optional(),
    return_handling_agents_contact_number: z.string().optional().optional(),
  }),
  shape: z.object({
    return_order_number: z.string().optional(),
    return_aircraft: z.string().optional(),
    return_tail_number: z.string(),
    return_call_sign: z.string().optional(),
    return_operator: z.string(),
    return_departure_date: z.string(),
    return_departure_time: z.string(),
    return_departure_location: z.string().optional(),
    return_arrival_location: z.string(),
    return_arrival_date: z.string(),
    return_arrival_time: z.string(),
    return_duration_hours: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
    return_duration_minutes: z
      .union([z.literal(""), z.coerce.number().optional()])
      .optional(),
    return_handling_agents_country_code: z.string().optional(),
    return_handling_agents: z.string().optional(),
    return_handling_agents_contact_number: z.string().optional(),
  }),
});

// 1. Re-define your base schema with the type discriminators
const getBaseSchema = (t: any) =>
  z.object({
    companion_ids: z.array(z.number()),
    user_id: z.union([z.number(), z.string()]).optional(),
    has_user: z.boolean().optional(),
    admin_notes: z.string().optional(),
    other_notes: z.string().optional(),
    is_user_verify_all_information: z.boolean(),
    // Add the discriminators
    outbound_flight_type: z.union([z.literal("1"), z.literal("2")]),
    return_flight_type: z.union([
      z.literal("0"),
      z.literal("1"),
      z.literal("2"),
    ]),
    flight_ticket: z.string().optional(),
  });

// 3. Use superRefine to add back conditional validation
export const getFlightFormSchema = (t: any) => {
  const outboundCommercial = getOutboundCommercial(t);
  const outboundPrivateJet = getOutboundPrivateJet(t);
  const returnCommercial = getReturnCommercial(t);
  const returnPrivateJet = getReturnPrivateJet(t);

  const outboundCommercialShapeForMerge = outboundCommercial.shape
    .partial()
    .extend({
      outbound_departure_airport_code: z.string().nullable().optional(),
      outbound_departure_airport_name: z.string().nullable().optional(),
      outbound_arrival_airport_code: z.string().nullable().optional(),
      outbound_arrival_airport_name: z.string().nullable().optional(),
    });

  const baseWithAllFieldsOptional = getBaseSchema(t).merge(
    outboundCommercialShapeForMerge
      .merge(outboundPrivateJet.shape.partial()) // <-- Use the SHAPE
      .merge(returnCommercial.shape.partial()) // <-- Use the SHAPE
      .merge(returnPrivateJet.shape.partial()), // <-- Use the SHAPE
  );

  return baseWithAllFieldsOptional.superRefine((data, ctx) => {
    // --- Validate Passengers ---
    if (data.companion_ids.length === 0 && !data.has_user) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: t("flightForm.validation.atLeastOneAttendee"),
        path: ["companion_ids"],
      });
    }

    // --- Validate Outbound ---
    if (data.outbound_flight_type === "1") {
      const result = outboundPrivateJet.schema.safeParse(data); // <-- Use VALIDATED schema
      if (!result.success) {
        (result as SafeParseError<FlightFormInput>).error.errors.forEach(
          (err) => {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: err.message,
              path: err.path,
            });
          },
        );
      }
    } else if (data.outbound_flight_type === "2") {
      const result = outboundCommercial.schema.safeParse(data); // <-- Use VALIDATED schema
      if (!result.success) {
        (result as SafeParseError<FlightFormInput>).error.errors.forEach(
          (err) => {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: err.message,
              path: err.path,
            });
          },
        );
      }
    }

    // --- Validate Return ---
    if (data.return_flight_type === "1") {
      const result = returnPrivateJet.schema.safeParse(data); // <-- Use VALIDATED schema
      if (!result.success) {
        (result as SafeParseError<FlightFormInput>).error.errors.forEach(
          (err) => {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: err.message,
              path: err.path,
            });
          },
        );
      }
    } else if (data.return_flight_type === "2") {
      const result = returnCommercial.schema.safeParse(data); // <-- Use VALIDATED schema
      if (!result.success) {
        (result as SafeParseError<FlightFormInput>).error.errors.forEach(
          (err) => {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: err.message,
              path: err.path,
            });
          },
        );
      }
    }
  });
};

// Original schema for types (can use a dummy translator)
const dummyT = (key: string) => key;
export const FlightFormSchema = getFlightFormSchema(dummyT);

export type FlightFormSchemaType = z.infer<typeof FlightFormSchema>;
export type FlightFormInput = z.input<typeof FlightFormSchema>;
export type FlightFormOutput = z.output<typeof FlightFormSchema>;
